import { useState, useCallback, type RefObject } from 'react';
import type { ViewState } from '../types/warehouse.types';
import { DRAG_THRESHOLD } from '../constants/warehouse.constants';

interface UseCanvasPanningProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

interface UseCanvasPanningResult {
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
}

export const useCanvasPanning = ({
  canvasRef,
  viewState,
  setViewState
}: UseCanvasPanningProps): UseCanvasPanningResult => {
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Middle button (1) or Right button (2) - always start panning immediately
    if (e.button === 1 || e.button === 2) {
      setIsDragging(true);
      setIsMouseDown(true);
      setDragStartPos({ x, y });
      setLastMousePos({ x, y });
      e.preventDefault();
      return;
    }

    // Left button (0) - prepare to pan (wait for threshold)
    if (e.button === 0) {
      setIsMouseDown(true);
      setDragStartPos({ x, y });
      setLastMousePos({ x, y });
    }
  }, [canvasRef]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check for drag threshold if mouse is down but not yet dragging
    if (isMouseDown && !isDragging) {
      const deltaX = x - dragStartPos.x;
      const deltaY = y - dragStartPos.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > DRAG_THRESHOLD) {
        // Exceeded threshold - start panning
        setIsDragging(true);
      }
    }

    // Handle panning
    if (isDragging) {
      const dx = x - lastMousePos.x;
      const dy = y - lastMousePos.y;

      setViewState(prev => {
        // Un-rotate the mouse delta to align with the world coordinates
        const cos = Math.cos(-prev.rotation);
        const sin = Math.sin(-prev.rotation);
        const rotatedDx = dx * cos - dy * sin;
        const rotatedDy = dx * sin + dy * cos;

        return {
          ...prev,
          offsetX: prev.offsetX + rotatedDx / prev.scale,
          offsetY: prev.offsetY + rotatedDy / prev.scale
        };
      });

      setLastMousePos({ x, y });
    }
  }, [isDragging, isMouseDown, dragStartPos, lastMousePos, canvasRef, setViewState]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsMouseDown(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    setIsMouseDown(false);
  }, []);

  return {
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave
  };
};
